import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n/i18n';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setAuth } from '../../redux/authSlice';
import { useSnackbar } from 'notistack';
import ImageUploadCrop from '../util/ImageUploadCrop';
import CustomFieldValues from '../util/CustomFieldValues';
import Select from 'react-select';
import Tooltip from '@mui/material/Tooltip';
import UserToilHistory from '../toilsComponents/UserToilHistory';
import HrPatternTimeline from '../hrComponents/HrPatternTimeline';
import useIsMobile from '../util/useIsMobile';
import CalendarPicker from '../util/CalendarPicker';
import UserAvatarUtil from '../util/UserAvatar';

const COMPLETION_FIELDS = [
  { fieldKey: 'avatarUrl' }, { fieldKey: 'mobile_phone' }, { fieldKey: 'date_of_birth' },
  { fieldKey: 'sex' }, { fieldKey: 'city' }, { fieldKey: 'address' }, { fieldKey: 'postcode' },
  { fieldKey: 'job_title' }, { fieldKey: 'departmentId' }, { fieldKey: 'contract_start_date' },
  { fieldKey: 'time_zone' }, { fieldKey: 'emergency_contact_name' }, { fieldKey: 'emergency_contact_phone' },
  { fieldKey: 'second_name' }, { fieldKey: 'emergency_contact_relationship' },
];

function calcCompletion(u: any, configs: any[]): number {
  let total = 0, filled = 0;
  for (const f of COMPLETION_FIELDS) {
    const cfg = configs.find((c) => c.fieldKey === f.fieldKey);
    if (cfg && !cfg.isVisible) continue;
    const weight = cfg?.weight ?? 1;
    total += weight;
    const val = u?.[f.fieldKey];
    if (val !== null && val !== undefined && val !== '') filled += weight;
  }
  return total > 0 ? Math.round((filled / total) * 100) : 0;
}

interface Timezone { id: number; name: string; utc_offset: string; }
interface UserBasic { id: number; first_name: string; last_name: string; supervisorId?: number; avatarUrl?: string; job_title?: string; }

interface Props {
  selectedUser: { id: number; first_name: string; last_name: string };
  isSelf?: boolean;
  hrMode?: boolean;              // открыта из HR → Employees: доп. HR-вкладки/действия, без Settings
  users?: UserBasic[];
  access?: any;
  setShow: (v: boolean) => void;
  refreshUsers?: () => void;
  refreshUserHeder: () => void;
  baseUrl: string;
}

// ── Styles ────────────────────────────────────────────────────────────────────

const inp: React.CSSProperties = {
  width: '100%', fontSize: 13, padding: '7px 11px', borderRadius: 9,
  border: '1px solid var(--border-new, rgba(0,10,60,.08))',
  background: 'var(--surface2, rgba(255,255,255,.55))',
  color: 'var(--text-color)', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
};
const sel: React.CSSProperties = { ...inp, height: 34, padding: '0 9px' };

// ── Sub-components ────────────────────────────────────────────────────────────

function Acc({ icon, title, children, open: initOpen = true }: {
  icon: string; title: string; children: React.ReactNode; open?: boolean;
}) {
  const [open, setOpen] = useState(initOpen);
  return (
    <div style={{ background: 'var(--surface2, rgba(255,255,255,.55))', border: '1px solid var(--border2-new, rgba(0,10,60,.05))', borderRadius: 12, overflow: 'hidden', marginBottom: 8 }}>
      <div onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer', userSelect: 'none' }}
        onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'rgba(0,10,60,.03)'}
        onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'transparent'}
      >
        <span style={{ fontSize: 13, flexShrink: 0 }}>{icon}</span>
        <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-color-calendar)', flex: 1 }}>{title}</span>
        <span style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'none' }}>▾</span>
      </div>
      {open && <div style={{ borderTop: '1px solid var(--border2-new, rgba(0,10,60,.05))' }}>{children}</div>}
    </div>
  );
}

function FR({ label, value, muted }: { label: string; value?: string | null; muted?: boolean }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', padding: '7px 14px', gap: 12, borderBottom: '1px solid var(--border2-new, rgba(0,10,60,.05))' }}>
      <span style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', minWidth: 130, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, color: muted ? 'var(--text-color-calendar)' : 'var(--text-color)', fontWeight: 500, flex: 1, textAlign: 'right' }}>{value}</span>
    </div>
  );
}

function EF({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ padding: '9px 14px', borderBottom: '1px solid var(--border2-new, rgba(0,10,60,.05))' }}>
      <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 5, fontWeight: 700 }}>{label}</div>
      {children}
    </div>
  );
}

function FREdit({ label, value, onChange, type = 'text', placeholder, options, onBlur, loading }: {
  label: string; value: string; onChange: (v: string) => void;
  type?: string; placeholder?: string;
  options?: { value: string; label: string }[];
  onBlur?: () => void;
  loading?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  const fs: React.CSSProperties = {
    flex: 1, fontSize: 13, fontWeight: 500, minWidth: 0, fontFamily: 'inherit',
    border: `1px solid ${focused ? 'var(--border-input)' : 'transparent'}`,
    background: focused ? 'var(--bg-input)' : 'transparent',
    color: 'var(--text-color)', outline: 'none', padding: '2px 6px', borderRadius: 6,
    textAlign: 'right' as const, cursor: 'text',
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', padding: '7px 14px', gap: 12, borderBottom: '1px solid var(--border2-new, rgba(0,10,60,.05))' }}>
      <span style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', minWidth: 130, flexShrink: 0 }}>{label}</span>
      {options ? (
        <select style={fs} value={value} onChange={e => onChange(e.target.value)}
          onFocus={() => setFocused(true)} onBlur={() => { setFocused(false); onBlur?.(); }}>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : type === 'date' ? (
        <CalendarPicker mode="single" variant="field" output="string" value={value} onChange={onChange} />
      ) : (
        <input type={type} value={value} placeholder={placeholder} style={fs}
          onChange={e => onChange(e.target.value)}
          onFocus={() => setFocused(true)} onBlur={() => { setFocused(false); onBlur?.(); }} />
      )}
      {loading && <span style={{ fontSize: 10, color: 'var(--text-color-calendar-week)', flexShrink: 0 }}>…</span>}
    </div>
  );
}

function Tog({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <div onClick={() => onChange(!checked)}
      style={{ display: 'flex', alignItems: 'center', padding: '9px 14px', gap: 12, borderBottom: '1px solid var(--border2-new, rgba(0,10,60,.05))', cursor: 'pointer' }}
      onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'rgba(0,10,60,.03)'}
      onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'transparent'}
    >
      <span style={{ flex: 1, fontSize: 12, color: 'var(--text-color-calendar)' }}>{label}</span>
      <div style={{ width: 36, height: 20, borderRadius: 10, background: checked ? '#10b981' : 'rgba(0,10,60,.1)', position: 'relative', flexShrink: 0, transition: 'background .2s' }}>
        <div style={{ position: 'absolute', width: 14, height: 14, borderRadius: '50%', background: 'white', top: 3, left: checked ? 19 : 3, transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }} />
      </div>
    </div>
  );
}

function UserAvatar({ user, baseUrl, size = 68 }: { user: any; baseUrl: string; size?: number }) {
  const initials = `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`;
  if (user.avatarUrl) {
    return <img src={`${baseUrl}${user.avatarUrl}`} alt={initials} style={{ width: size, height: size, borderRadius: 14, objectFit: 'cover', border: '2px solid rgba(255,255,255,.3)', boxShadow: '0 4px 16px rgba(0,0,0,.25)', flexShrink: 0 }} />;
  }
  return (
    <div style={{ width: size, height: size, borderRadius: 14, background: 'rgba(255,255,255,.15)', border: '2px solid rgba(255,255,255,.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.33, fontWeight: 900, color: 'white', flexShrink: 0, boxShadow: '0 4px 16px rgba(0,0,0,.2)' }}>
      {initials}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

const ProfileCard: React.FC<Props> = ({ selectedUser, isSelf = false, hrMode = false, users = [], access, setShow, refreshUsers, refreshUserHeder, baseUrl }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const isAdmin = !!(access?.users_edit);
  const hrEdit = hrMode && isAdmin;
  const peerMode = !isSelf && !hrMode; // viewing someone else without HR access

  const [tab, setTab] = useState<'about' | 'work' | 'settings' | 'absences' | 'toil' | 'pattern'>('about');
  const bodyRef = useRef<HTMLDivElement>(null);
  const [bodyScrolled, setBodyScrolled] = useState(false); // Save виден только после скролла вниз
  useEffect(() => { setBodyScrolled(false); if (bodyRef.current) bodyRef.current.scrollTop = 0; }, [tab]);
  const [userData, setUserData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<any>({});
  const [avatarUrl, setAvatarUrl] = useState('');

  const [departments, setDepartments] = useState<any[]>([]);
  const [patterns, setPatterns] = useState<any[]>([]);
  const [workingStatuses, setWorkingStatuses] = useState<any[]>([]);
  const [rolesList, setRolesList] = useState<any[]>([]);
  const [carsList, setCarsList] = useState<any[]>([]);
  const [timezones, setTimezones] = useState<Timezone[]>([]);
  const [allUsers, setAllUsers] = useState<UserBasic[]>([]);
  const [genders, setGenders] = useState<{ value: string; label_en: string; label_ua: string; label_de: string }[]>([]);
  const [fieldConfigs, setFieldConfigs] = useState<any[]>([]);

  const [notifs, setNotifs] = useState({ mail_isPendingRep: false, mail_isApprovedRep: false, mail_isDocument: false, mail_isWeekReminder: false, mail_isToApproveReminder: false, mail_isExpensesPaid: false, mail_isExpensesFulfilled: false, mail_isExpensesReject: false });
  const [language, setLanguage] = useState('en');
  const [timezone, setTimezone] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const pwdRef = useRef<HTMLInputElement>(null);
  const pwdConfirmRef = useRef<HTMLInputElement>(null);
  const [postcodeLoading, setPostcodeLoading] = useState(false);
  const [hasCustomFields, setHasCustomFields] = useState(true);

  // HR-режим: отсутствия, типы, смена паттерна, сброс пароля, тумблер активности
  const [absences, setAbsences] = useState<any[]>([]);
  const [absenceTypes, setAbsenceTypes] = useState<any[]>([]);
  const [showResetPwd, setShowResetPwd] = useState(false);
  const [togglingActive, setTogglingActive] = useState(false);

  // Manual activation for invite-pending users
  const [activatePassword, setActivatePassword] = useState('');
  const [activateConfirm, setActivateConfirm] = useState('');
  const [activateRoleId, setActivateRoleId] = useState<number | ''>('');
  const [activating, setActivating] = useState(false);
  const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*()\-_=+[\]{};':"\\|,.<>/?`~]).{8,}$/;

  const handleActivate = async () => {
    if (!activatePassword || !activateConfirm) { enqueueSnackbar('Enter password and confirm', { variant: 'error' }); return; }
    if (activatePassword !== activateConfirm) { enqueueSnackbar('Passwords do not match', { variant: 'error' }); return; }
    if (!PASSWORD_REGEX.test(activatePassword)) { enqueueSnackbar(t('PasswordRequirements'), { variant: 'error' }); return; }
    setActivating(true);
    try {
      const payload: any = { password: activatePassword, password_confirm: activateConfirm };
      if (activateRoleId) payload.roleId = activateRoleId;
      await axios.post(`activate-user/${selectedUser.id}`, payload);
      setUserData((prev: any) => prev ? { ...prev, profile_completed: true } : prev);
      setActivatePassword('');
      setActivateConfirm('');
      setActivateRoleId('');
      enqueueSnackbar('Account activated successfully', { variant: 'success' });
      refreshUsers?.();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message ?? 'Activation failed', { variant: 'error' });
    }
    setActivating(false);
  };

  // Driver license
  const [license, setLicense] = useState<any>(null);
  const [licenseForm, setLicenseForm] = useState<any>({ license_number: '', vehicle_class: '', issue_date: '', expiry_date: '' });
  const [licenseUploading, setLicenseUploading] = useState<'front' | 'back' | null>(null);

  const lookupPostcode = async () => {
    const pc = (form.postcode || '').replace(/\s/g, '');
    if (!pc || pc.length < 5) return;
    setPostcodeLoading(true);
    try {
      const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(pc)}`);
      const json = await res.json();
      if (json.status === 200 && json.result) {
        const city = json.result.admin_district || json.result.parish || json.result.region || '';
        if (city) setForm((p: any) => ({ ...p, city }));
      }
    } catch {}
    setPostcodeLoading(false);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`user/${selectedUser.id}`);
      setUserData(data);
      setAvatarUrl(data.avatarUrl ? `${baseUrl}${data.avatarUrl}` : '');
      setForm({
        first_name: data.first_name || '', last_name: data.last_name || '', second_name: data.second_name || '',
        email: data.email || '', mobile_phone: data.mobile_phone || '',
        city: data.city || '', postcode: data.postcode || '', address: data.address || '',
        date_of_birth: data.date_of_birth ? String(data.date_of_birth).slice(0, 10) : '', sex: data.sex || '',
        job_title: data.job_title || '', patternHoursPerWeek: data.pattern?.hoursPerWeek ?? null, min_leave_entitlement: data.min_leave_entitlement ?? '',
        vacationDays: data.vacationDays ?? '', salary_perHour: data.salary_perHour ?? '',
        contract_start_date: data.contract_start_date ? String(data.contract_start_date).slice(0, 10) : '',
        license_expiry_date: data.license_expiry_date ? String(data.license_expiry_date).slice(0, 10) : '',
        driving_licence_number: data.driving_licence_number || '',
        default_car_id: data.default_car_id ?? null,
        probation_start_date: data.probation_start_date ? String(data.probation_start_date).slice(0, 10) : '',
        probation_end_date: data.probation_end_date ? String(data.probation_end_date).slice(0, 10) : '',
        notice_period: data.notice_period || '',
        time_zone: data.time_zone || '', notification_language: data.notification_language || '',
        workingStatusId: data.workingStatusId ?? null, departmentId: data.departmentId ?? null,
        patternId: data.patternId ?? null, supervisorId: data.supervisorId ?? null,
        isActive: data.isActive ?? true, isManager: data.isManager ?? false,
        rolesId: (data.roles || []).map((r: any) => r.id),
        mail_isPendingRep: !!data.mail_isPendingRep, mail_isApprovedRep: !!data.mail_isApprovedRep,
        mail_isDocument: !!data.mail_isDocument, mail_isWeekReminder: !!data.mail_isWeekReminder,
        mail_isToApproveReminder: !!data.mail_isToApproveReminder, mail_isExpensesPaid: !!data.mail_isExpensesPaid,
        mail_isExpensesFulfilled: !!data.mail_isExpensesFulfilled, mail_isExpensesReject: !!data.mail_isExpensesReject,
        emergency_contact_name: data.emergency_contact_name || '',
        emergency_contact_relationship: data.emergency_contact_relationship || '',
        emergency_contact_phone: data.emergency_contact_phone || '',
        emergency_contact_email: data.emergency_contact_email || '',
      });
      setNotifs({ mail_isPendingRep: !!data.mail_isPendingRep, mail_isApprovedRep: !!data.mail_isApprovedRep, mail_isDocument: !!data.mail_isDocument, mail_isWeekReminder: !!data.mail_isWeekReminder, mail_isToApproveReminder: !!data.mail_isToApproveReminder, mail_isExpensesPaid: !!data.mail_isExpensesPaid, mail_isExpensesFulfilled: !!data.mail_isExpensesFulfilled, mail_isExpensesReject: !!data.mail_isExpensesReject });
      setLanguage(data.notification_language || localStorage.getItem('language') || localStorage.getItem('i18nextLng') || 'en');
      setTimezone(data.time_zone || '');
    } catch {}
    setLoading(false);
  }, [selectedUser.id, baseUrl]);

  const fetchLicense = useCallback(async () => {
    try {
      const { data } = await axios.get(`hr-driver-license/user/${selectedUser.id}`);
      setLicense(data);
      setLicenseForm({
        license_number: data?.license_number || '',
        vehicle_class: data?.vehicle_class || '',
        issue_date: data?.issue_date ? String(data.issue_date).slice(0, 10) : '',
        expiry_date: data?.expiry_date ? String(data.expiry_date).slice(0, 10) : '',
      });
    } catch {
      setLicense(null);
      setLicenseForm({ license_number: '', vehicle_class: '', issue_date: '', expiry_date: '' });
    }
  }, [selectedUser.id]);

  useEffect(() => {
    fetchData();
    fetchLicense();
    Promise.allSettled([
      axios.get('users'), axios.get('department'), axios.get('hr-schedule-template'),
      axios.get('hr-working-status'), axios.get('roles'), axios.get('static-timezone'),
      axios.get('static-gender'), axios.get('hr/profile-field-config'), axios.get('cars'),
    ]).then(([us, dep, cont, ws, roles, tz, gen, fc, cars]) => {
      if (us.status === 'fulfilled') setAllUsers(Array.isArray(us.value.data) ? us.value.data : []);
      if (dep.status === 'fulfilled') setDepartments(Array.isArray(dep.value.data) ? dep.value.data.filter((d: any) => d.active) : []);
      if (cont.status === 'fulfilled') setPatterns(Array.isArray(cont.value.data) ? cont.value.data : []);
      if (ws.status === 'fulfilled') setWorkingStatuses(Array.isArray(ws.value.data) ? ws.value.data : []);
      if (roles.status === 'fulfilled') setRolesList(Array.isArray(roles.value.data) ? roles.value.data : []);
      if (tz.status === 'fulfilled') setTimezones(Array.isArray(tz.value.data) ? tz.value.data : []);
      if (gen.status === 'fulfilled') setGenders(Array.isArray(gen.value.data) ? gen.value.data : []);
      if (fc.status === 'fulfilled') setFieldConfigs(Array.isArray(fc.value.data) ? fc.value.data : []);
      if (cars.status === 'fulfilled') setCarsList(Array.isArray(cars.value.data) ? cars.value.data : []);
    });
  }, [fetchData, fetchLicense]);

  // HR-режим: отсутствия сотрудника + типы (для вкладки Absences)
  useEffect(() => {
    if (!hrMode) return;
    Promise.allSettled([
      axios.get(`hr-absence?userId=${selectedUser.id}`),
      axios.get('hr-absence-type'),
    ]).then(([abs, tp]) => {
      if (abs.status === 'fulfilled') setAbsences(Array.isArray(abs.value.data) ? abs.value.data : []);
      if (tp.status === 'fulfilled') setAbsenceTypes(Array.isArray(tp.value.data) ? tp.value.data : []);
    });
  }, [hrMode, selectedUser.id]);

  const handleSaveProfile = async () => {
    const payload: any = {
      ...form,
      date_of_birth: form.date_of_birth || null,
      contract_start_date: form.contract_start_date || null,
      license_expiry_date: form.license_expiry_date || null,
      default_car_id: form.default_car_id || null,
      probation_start_date: form.probation_start_date || null,
      probation_end_date: form.probation_end_date || null,
      notice_period: form.notice_period || null,
      min_leave_entitlement: form.min_leave_entitlement !== '' ? Number(form.min_leave_entitlement) : null,
      vacationDays: form.vacationDays !== '' ? Number(form.vacationDays) : null,
      salary_perHour: form.salary_perHour !== '' ? Number(form.salary_perHour) : null,
      workingStatusId: form.workingStatusId || null, departmentId: form.departmentId || null,
      patternId: form.patternId || null, supervisorId: form.supervisorId || null,
    };
    // Паттерн и дата поступления в HR-режиме меняются на вкладке Pattern (таймлайн), не через generic-save
    if (hrMode) { delete payload.patternId; delete payload.contract_start_date; }
    try {
      await axios.put(`update-user/${selectedUser.id}`, payload);
      await fetchData();
      refreshUserHeder();
      refreshUsers?.();
      setEditing(false);
      enqueueSnackbar(t('SavedSuccessfully') || 'Saved', { variant: 'success' });
    } catch { enqueueSnackbar(t('ErrorSaving') || 'Error', { variant: 'error' }); }
  };

  // HR-режим: включить/выключить учётную запись
  const toggleActive = async () => {
    const next = !(userData?.isActive !== false);
    setTogglingActive(true);
    try {
      await axios.put(`update-user/${selectedUser.id}`, { isActive: next });
      await fetchData();
      refreshUsers?.();
      enqueueSnackbar(t('SavedSuccessfully') || 'Saved', { variant: 'success' });
    } catch { enqueueSnackbar(t('ErrorSaving') || 'Error', { variant: 'error' }); }
    setTogglingActive(false);
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`update-user/${selectedUser.id}`, { ...notifs, notification_language: language, time_zone: timezone });
      if (isSelf && language && ['en', 'uk', 'ua', 'de'].includes(language)) {
        await i18n.changeLanguage(language);
        localStorage.setItem('i18nextLng', language);
      }
      refreshUserHeder();
      enqueueSnackbar(t('SavedSuccessfully') || 'Saved', { variant: 'success' });
    } catch { enqueueSnackbar(t('ErrorSaving') || 'Error', { variant: 'error' }); }
    setSavingSettings(false);
  };

  const handleChangePwd = async () => {
    const pwd = pwdRef.current?.value; const confirm = pwdConfirmRef.current?.value;
    if (!pwd || !confirm) { enqueueSnackbar(t('EnterPassword'), { variant: 'error' }); return; }
    if (pwd !== confirm) { enqueueSnackbar(t('PasswordsDoNotMatch'), { variant: 'error' }); return; }
    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*()\-_=+[\]{};':"\\|,.<>/?`~]).{8,}$/.test(pwd)) { enqueueSnackbar(t('PasswordRequirements'), { variant: 'error' }); return; }
    try { await axios.put(`password/${selectedUser.id}`, { password: pwd, password_confirm: confirm }); enqueueSnackbar(t('PasswordChanged'), { variant: 'success' }); setShowPwd(false); setShowResetPwd(false); }
    catch { enqueueSnackbar(t('ErrorSaving') || 'Error', { variant: 'error' }); }
  };

  const handleAvatarUpload = async (file: File) => {
    if (file.size > 5 * 1024 * 1024) { enqueueSnackbar(t('PhotoTooLarge'), { variant: 'error' }); return; }
    const fd = new FormData(); fd.append('avatar', file);
    try {
      const { data } = await axios.post('upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAvatarUrl(`${baseUrl}${data.avatarUrl}?t=${Date.now()}`);
      refreshUserHeder();
      enqueueSnackbar(t('PhotoUpdated'), { variant: 'success' });
    } catch (err: any) { enqueueSnackbar(err?.response?.data?.message || t('PhotoUploadError'), { variant: 'error' }); }
  };

  const saveLicense = async () => {
    try {
      await axios.put(`hr-driver-license/user/${selectedUser.id}`, licenseForm);
      await fetchLicense();
      enqueueSnackbar(t('SavedSuccessfully') || 'Saved', { variant: 'success' });
    } catch { enqueueSnackbar(t('ErrorSaving') || 'Error', { variant: 'error' }); }
  };

  const uploadLicensePhoto = async (side: 'front' | 'back', file: File) => {
    setLicenseUploading(side);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`hr-driver-license/user/${selectedUser.id}/photo-${side}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await fetchLicense();
      enqueueSnackbar(t('PhotoUpdated') || 'Uploaded', { variant: 'success' });
    } catch { enqueueSnackbar(t('ErrorSaving') || 'Error', { variant: 'error' }); }
    setLicenseUploading(null);
  };

  const handleLogout = async () => {
    try { await axios.post('logout', {}, { withCredentials: true }); axios.defaults.headers.common['Authorization'] = ''; localStorage.removeItem('authToken'); dispatch(setAuth(false)); navigate('/login'); } catch {}
  };

  const sf = (k: string) => (v: any) => setForm((p: any) => ({ ...p, [k]: v }));
  const fv = (k: string) => form[k] ?? '';
  const fmtDate = (v: any) => v ? new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : null;

  const genderLabel = (g: { label_en: string; label_ua: string; label_de: string }) => {
    const lng = i18n.language;
    if (lng === 'uk' || lng === 'ua') return g.label_ua;
    if (lng === 'de') return g.label_de;
    return g.label_en;
  };

  const u = userData || selectedUser;
  const initials = `${u.first_name?.[0] || ''}${u.last_name?.[0] || ''}`;
  const allUsersResolved = allUsers.length ? allUsers : users;
  const supervisor = allUsersResolved.find(us => us.id === u?.supervisorId);
  const team = allUsersResolved.filter(us => us.supervisorId === u?.id);
  const deptName = departments.find(d => d.id === u?.departmentId)?.title;
  const contractName = patterns.find(c => c.id === u?.patternId)?.name;
  const wsNow = workingStatuses.find(w => w.id === u?.workingStatusId);

  const canEdit = isSelf || isAdmin;

  const isFieldVisible = (fieldKey: string) => {
    const cfg = fieldConfigs.find((c) => c.fieldKey === fieldKey);
    return cfg ? cfg.isVisible : true;
  };

  const completionPct = userData ? calcCompletion(userData, fieldConfigs) : 0;

  const slStyles = {
    control: (b: any) => ({ ...b, background: 'var(--bg-input)', borderColor: 'var(--border-input)', minHeight: 31, fontSize: 13 }),
    menu: (b: any) => ({ ...b, background: 'var(--bg-form)', zIndex: 9999 }),
    option: (b: any, s: any) => ({ ...b, background: s.isFocused ? 'var(--btn-user-form)' : 'var(--bg-form)', color: 'var(--text-color)', fontSize: 13 }),
    singleValue: (b: any) => ({ ...b, color: 'var(--text-color)' }),
    multiValue: (b: any) => ({ ...b, background: 'var(--btn-user-btn)' }),
    multiValueLabel: (b: any) => ({ ...b, color: 'var(--text-color)', fontSize: 11 }),
    input: (b: any) => ({ ...b, color: 'var(--text-color)' }),
    placeholder: (b: any) => ({ ...b, color: 'var(--text-color-calendar-week)', fontSize: 12 }),
    indicatorSeparator: () => ({}),
  };

  // Паттерн для недельного графика (Schedule) — из назначенного паттерна сотрудника
  const schedPattern = patterns.find((p: any) => p.id === u?.patternId) || u?.pattern || null;
  const minutesBetween = (start?: string, end?: string) => {
    if (!start || !end) return 0;
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    return Math.max(0, (eh * 60 + em) - (sh * 60 + sm));
  };
  const dayNet = (d: any) => d && !d.dayOff ? Math.round((minutesBetween(d.start, d.end) - (d.breakMins || 0)) / 6) / 10 : 0;
  const absColor = (typeId: number) => absenceTypes.find((x: any) => x.id === typeId)?.color || 'var(--text-color-calendar)';
  const absTypeName = (id: number) => absenceTypes.find((x: any) => x.id === id)?.name || '—';

  // ── Tab D nav style ──────────────────────────────────────────────────────────
  const tabs: { id: 'about' | 'work' | 'settings' | 'absences' | 'toil' | 'pattern'; label: string; icon: string }[] = hrMode
    ? [
        { id: 'about', label: t('About') || 'About', icon: '👤' },
        { id: 'work', label: t('Work') || 'Work', icon: '💼' },
        { id: 'pattern', label: t('HrWorkingPattern') || 'Working pattern', icon: '📐' },
        { id: 'absences', label: t('HrAbsences') || 'Absences', icon: '🌴' },
        { id: 'toil', label: t('Toil') || 'TOIL', icon: '⏱️' },
      ]
    : [
        { id: 'about', label: t('About') || 'About', icon: '👤' },
        { id: 'work', label: t('Work') || 'Work', icon: '💼' },
        { id: 'settings', label: t('Settings') || 'Settings', icon: '⚙️' },
      ];

  const tabStyle = (active: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 5,
    flexShrink: 0,
    padding: '11px 14px 9px',
    border: 'none', background: 'none',
    color: active ? '#4c1d95' : 'var(--text-color-calendar-week)',
    fontSize: 12, fontWeight: 600, letterSpacing: '.01em',
    cursor: 'pointer',
    position: 'relative',
    transition: 'color .16s',
  });

  if (loading) return (
    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-form, #fff)' }}>
      <div style={{ fontSize: 13, color: 'var(--text-color-calendar-week)' }}>Loading…</div>
    </div>
  );

  // ── PEER MODE: basic read-only view ─────────────────────────────────────────
  if (peerMode && userData) {
    const u = userData;
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg-form, #fff)', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ position: 'relative', flexShrink: 0, height: 110, background: 'linear-gradient(135deg, #1e3a8a 0%, #4c1d95 55%, #831843 100%)', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: 0, opacity: .05, backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'.7\' numOctaves=\'4\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")', backgroundSize: '256px', pointerEvents: 'none' }} />
          <button type="button" onClick={() => setShow(false)}
            style={{ position: 'absolute', top: 14, right: 14, zIndex: 5, width: 30, height: 30, borderRadius: 9, background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.2)', color: 'rgba(255,255,255,.8)', fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            ✕
          </button>
          <div style={{ position: 'relative', zIndex: 1, height: '100%', padding: '0 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
            <UserAvatarUtil user={u} baseUrl={baseUrl} size={64} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 18, fontWeight: 900, color: 'white', letterSpacing: '-.02em' }}>{u.first_name}{u.second_name ? ` ${u.second_name}` : ''} {u.last_name}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,.7)', marginTop: 2 }}>{u.job_title || ''}</div>
              {deptName && <span style={{ marginTop: 6, display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: 'rgba(255,255,255,.15)', color: 'rgba(255,255,255,.85)', border: '1px solid rgba(255,255,255,.2)' }}>{deptName}</span>}
            </div>
          </div>
        </div>
        {/* Basic info */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px', scrollbarWidth: 'thin' }}>
          <div style={{ background: 'var(--surface2, rgba(0,10,60,.03))', border: '1px solid var(--border2-new, rgba(0,10,60,.05))', borderRadius: 12, overflow: 'hidden' }}>
            {u.email    && <FR label="Email"      value={u.email} />}
            {u.mobile_phone && <FR label="Phone"  value={u.mobile_phone} />}
            {deptName   && <FR label="Department" value={deptName} />}
            {u.job_title && <FR label="Job title" value={u.job_title} />}
            {wsNow      && <FR label="Status"     value={`${wsNow.emoji} ${wsNow.name}`} />}
          </div>
        </div>
      </div>
    );
  }

  // Бейджи (отдел / рабочий график / неактивность) + прогресс заполнения — переиспользуются
  // под именем (десктоп / обычный просмотр) или в нижней строке действий (мобильный HR-режим)
  const headerBadges = (
    <>
      {deptName && <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: 'rgba(255,255,255,.15)', color: 'rgba(255,255,255,.9)', border: '1px solid rgba(255,255,255,.2)' }}>{deptName}</span>}
      {wsNow && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'rgba(16,185,129,.2)', border: '1px solid rgba(16,185,129,.3)', borderRadius: 20, padding: '3px 10px', fontSize: 10, fontWeight: 700, color: '#6ee7b7' }}><span style={{ width: 6, height: 6, borderRadius: '50%', background: '#6ee7b7' }} />{wsNow.emoji} {wsNow.name}</span>}
      {u.isActive === false && <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: 'rgba(239,68,68,.2)', color: '#fca5a5', border: '1px solid rgba(239,68,68,.3)' }}>Inactive</span>}
      <Tooltip title={`${t('ProfileCompletion') || 'Profile'}: ${completionPct}%`} placement="bottom" arrow>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 80, height: 4, borderRadius: 4, background: 'rgba(255,255,255,.2)', overflow: 'hidden' }}>
            <div style={{ width: `${completionPct}%`, height: '100%', borderRadius: 4, background: completionPct >= 80 ? '#10b981' : completionPct >= 50 ? '#f59e0b' : '#ef4444', transition: 'width .4s' }} />
          </div>
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,.7)', fontWeight: 600 }}>{completionPct}%</span>
        </div>
      </Tooltip>
    </>
  );

  return (
    <>
    <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', background: 'transparent', overflow: 'hidden' }}>

      {/* ── GRADIENT HEADER ── */}
      <div style={{ position: 'relative', flexShrink: 0, height: isMobile && hrEdit ? 128 : 100, background: 'linear-gradient(135deg, #1e3a8a 0%, #4c1d95 55%, #831843 100%)', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, opacity: .05, backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'.7\' numOctaves=\'4\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")', backgroundSize: '256px', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', width: 220, height: 220, borderRadius: '50%', background: 'radial-gradient(circle, rgba(96,165,250,.28) 0%, transparent 70%)', top: -80, left: -40, pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(167,139,250,.2) 0%, transparent 70%)', bottom: -50, right: 120, pointerEvents: 'none' }} />

        {/* Close */}
        <Tooltip title={t('Close') || 'Close'} placement="bottom" arrow>
          <button type="button" onClick={() => setShow(false)}
            style={{ position: 'absolute', top: 14, right: isSelf ? 54 : 14, zIndex: 5, width: 30, height: 30, borderRadius: 9, background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.2)', color: 'rgba(255,255,255,.8)', fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            ✕
          </button>
        </Tooltip>
        {/* Logout (own profile only) */}
        {isSelf && (
          <Tooltip title={t('LogOut')} placement="bottom" arrow>
            <button type="button" onClick={handleLogout}
              style={{ position: 'absolute', top: 14, right: 14, zIndex: 5, width: 30, height: 30, borderRadius: 9, background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.2)', color: 'rgba(255,255,255,.75)', fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              ⎋
            </button>
          </Tooltip>
        )}

        {/* HR-действия: тумблер активности + сброс пароля (слева от крестика) */}
        {hrEdit && (
          <div style={isMobile
            ? { position: 'absolute', left: 20, right: 14, bottom: 12, zIndex: 5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, rowGap: 8, flexWrap: 'wrap' }
            : { position: 'absolute', top: 14, right: 52, zIndex: 5, display: 'flex', alignItems: 'center', gap: 8 }}>
            {isMobile && (deptName
              ? <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: 'rgba(255,255,255,.15)', color: 'rgba(255,255,255,.95)', border: '1px solid rgba(255,255,255,.25)', whiteSpace: 'nowrap', flexShrink: 0 }}>{deptName}</span>
              : <span />)}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Tooltip title={(userData?.isActive !== false ? t('HrDisableAccount') : t('Activate')) || ''} placement="bottom" arrow>
              <div onClick={togglingActive ? undefined : toggleActive}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 30, padding: '0 10px', borderRadius: 9, background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.2)', cursor: togglingActive ? 'default' : 'pointer', opacity: togglingActive ? .6 : 1 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,.9)' }}>{userData?.isActive !== false ? (t('Active') || 'Active') : (t('Inactive') || 'Inactive')}</span>
                <div style={{ width: 34, height: 18, borderRadius: 20, background: userData?.isActive !== false ? '#10b981' : 'rgba(255,255,255,.3)', position: 'relative', transition: 'background .18s', flexShrink: 0 }}>
                  <div style={{ position: 'absolute', top: 2, left: userData?.isActive !== false ? 18 : 2, width: 14, height: 14, borderRadius: '50%', background: '#fff', transition: 'left .18s', boxShadow: '0 1px 3px rgba(0,0,0,.25)' }} />
                </div>
              </div>
            </Tooltip>
            <button type="button" onClick={() => setShowResetPwd(true)}
              style={{ height: 30, borderRadius: 9, background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.2)', color: 'rgba(255,255,255,.9)', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 11px' }}>
              🔑 {t('HrResetPassword') || 'Reset password'}
            </button>
            </div>
          </div>
        )}

        {/* Avatar + info */}
        <div style={{ position: 'relative', zIndex: 1, height: '100%', padding: isMobile && hrEdit ? '14px 20px 0' : '0 20px', display: 'flex', alignItems: isMobile && hrEdit ? 'flex-start' : 'center', gap: 18 }}>
          {isSelf ? (
            <ImageUploadCrop onUpload={handleAvatarUpload} outputWidth={200} outputHeight={200} quality={0.85}>
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <div style={{ width: 68, height: 68, borderRadius: 18, background: avatarUrl ? 'transparent' : 'rgba(255,255,255,.15)', border: '2px solid rgba(255,255,255,.25)', boxShadow: '0 6px 20px rgba(0,0,0,.25)', overflow: 'hidden', cursor: 'pointer' }}>
                  {avatarUrl
                    ? <img src={avatarUrl} alt={initials} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 900, color: 'white' }}>{initials}</div>}
                </div>
                <div style={{ position: 'absolute', bottom: -3, right: -3, width: 22, height: 22, borderRadius: 7, background: 'rgba(255,255,255,.2)', border: '2px solid rgba(255,255,255,.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, cursor: 'pointer' }}>📷</div>
                <div style={{ position: 'absolute', top: -3, right: -3, width: 15, height: 15, borderRadius: '50%', background: '#10b981', border: '2.5px solid rgba(255,255,255,.2)', pointerEvents: 'none' }} />
              </div>
            </ImageUploadCrop>
          ) : (
            <UserAvatar user={{ ...u, avatarUrl: u.avatarUrl }} baseUrl={baseUrl} size={68} />
          )}

          <div style={{ flex: 1, minWidth: 0, paddingRight: isMobile ? 40 : 0 }}>
            <div style={{ fontSize: 20, fontWeight: 900, color: 'white', letterSpacing: '-.02em' }}>
              {u.first_name}{u.second_name ? ` ${u.second_name}` : ''} {u.last_name}
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,.7)', marginTop: 3 }}>{u.job_title || ''}</div>
            {!(isMobile && hrEdit) && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8, alignItems: 'center' }}>
                {headerBadges}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── TAB BAR (Variant D) ── */}
      <div style={{ background: 'var(--bg-form, #fff)', borderBottom: '1px solid var(--border2-new, rgba(0,10,60,.07))', flexShrink: 0 }}>
        <div style={{ display: 'flex', padding: '0 8px', overflowX: 'auto', scrollbarWidth: 'none' }}>
          {tabs.map(({ id, label }) => {
            const active = tab === id;
            return (
              <button key={id} type="button" style={tabStyle(active)} onClick={() => { setTab(id); setEditing(false); }}>
                {label}
                {active && (
                  <span style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: 'calc(100% - 16px)', height: 3, borderRadius: '3px 3px 0 0', background: 'linear-gradient(90deg, #4c1d95, #831843)' }} />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── BODY ── */}
      <div ref={bodyRef} onScroll={(e) => setBodyScrolled((e.currentTarget as HTMLDivElement).scrollTop > 24)}
        style={{ flex: 1, overflowY: 'auto', padding: '16px 16px', scrollbarWidth: 'thin' }}>

        {/* ── Invite pending banner ── */}
        {userData && userData.profile_completed === false && isAdmin && (
          <div style={{ background: 'rgba(217,119,6,.08)', border: '1px solid rgba(217,119,6,.35)', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span style={{ background: 'rgba(217,119,6,.15)', color: '#b45309', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20 }}>
                Invite pending
              </span>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                User hasn't accepted the invite yet. Set a password to activate manually:
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 10, marginLeft: 4, color: 'var(--text-secondary)' }}>Password</label>
                <input type="password" className="w-full rounded-[5px] edit-input" value={activatePassword} onChange={e => setActivatePassword(e.target.value)} placeholder="Min 8 chars, A-z, symbol" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 10, marginLeft: 4, color: 'var(--text-secondary)' }}>Confirm</label>
                <input type="password" className="w-full rounded-[5px] edit-input" value={activateConfirm} onChange={e => setActivateConfirm(e.target.value)} placeholder="Repeat password" />
              </div>
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 10, marginLeft: 4, color: 'var(--text-secondary)' }}>Role</label>
              <select
                className="w-full rounded-[5px] edit-input"
                value={activateRoleId}
                onChange={e => setActivateRoleId(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">— select role —</option>
                {rolesList.map((r: any) => (
                  <option key={r.id} value={r.id}>{r.title}</option>
                ))}
              </select>
            </div>
            <button type="button" onClick={handleActivate} disabled={activating}
              style={{ background: '#b45309', color: '#fff', border: 'none', borderRadius: 5, padding: '5px 16px', fontSize: 12, fontWeight: 600, cursor: activating ? 'not-allowed' : 'pointer', opacity: activating ? 0.6 : 1 }}>
              {activating ? '…' : 'Activate account'}
            </button>
          </div>
        )}

        {/* ════ TAB: ABOUT ════ */}
        {tab === 'about' && (
          <>
            <Acc icon="👤" title="Personal">
              {canEdit ? (
                <>
                  <FREdit label="First name" value={fv('first_name')} onChange={sf('first_name')} />
                  <FREdit label="Last name" value={fv('last_name')} onChange={sf('last_name')} />
                  {isFieldVisible('second_name') && <FREdit label="Middle name" value={fv('second_name')} onChange={sf('second_name')} />}
                  {isFieldVisible('date_of_birth') && <FREdit label="Date of birth" value={fv('date_of_birth')} onChange={sf('date_of_birth')} type="date" />}
                  {isFieldVisible('sex') && (
                    <FREdit label={t('Gender')} value={fv('sex')} onChange={sf('sex')}
                      options={[
                        { value: '', label: '—' },
                        ...genders.map(g => ({ value: g.value, label: genderLabel(g) })),
                      ]} />
                  )}
                </>
              ) : (
                <>
                  <FR label="Full name" value={`${u.first_name}${u.second_name ? ' ' + u.second_name : ''} ${u.last_name}`} />
                  {isFieldVisible('date_of_birth') && <FR label="Date of birth" value={fmtDate(u.date_of_birth)} muted />}
                  {isFieldVisible('sex') && <FR label={t('Gender')} value={(() => { const g = genders.find(x => x.value === u.sex); return g ? genderLabel(g) : u.sex; })()} muted />}
                </>
              )}
            </Acc>

            <Acc icon="📇" title="Contacts">
              {canEdit ? (
                <>
                  <FREdit label="Email" value={fv('email')} onChange={sf('email')} type="email" />
                  {isFieldVisible('mobile_phone') && <FREdit label="Mobile" value={fv('mobile_phone')} onChange={sf('mobile_phone')} type="tel" />}
                  {isFieldVisible('city') && <FREdit label="City" value={fv('city')} onChange={sf('city')} />}
                  {isFieldVisible('postcode') && <FREdit label="Postcode" value={fv('postcode')} onChange={sf('postcode')} onBlur={lookupPostcode} loading={postcodeLoading} />}
                  {isFieldVisible('address') && <FREdit label="Address" value={fv('address')} onChange={sf('address')} />}
                </>
              ) : (
                <>
                  <FR label="Email" value={u.email} />
                  {isFieldVisible('mobile_phone') && <FR label="Mobile" value={u.mobile_phone} muted />}
                  {isFieldVisible('city') && <FR label="City" value={u.city} muted />}
                  {isFieldVisible('postcode') && <FR label="Postcode" value={u.postcode} muted />}
                  {isFieldVisible('address') && <FR label="Address" value={u.address} muted />}
                </>
              )}
            </Acc>

            {(isFieldVisible('emergency_contact_name') || isFieldVisible('emergency_contact_phone')) && (
              <Acc icon="🆘" title="Emergency Contact" open={!!(u.emergency_contact_name || u.emergency_contact_phone)}>
                {canEdit ? (
                  <>
                    {isFieldVisible('emergency_contact_name') && <FREdit label="Contact name" value={fv('emergency_contact_name')} onChange={sf('emergency_contact_name')} placeholder="Full name" />}
                    {isFieldVisible('emergency_contact_relationship') && (
                      <FREdit label={t('HrEcRelationship')} value={fv('emergency_contact_relationship')} onChange={sf('emergency_contact_relationship')}
                        options={[
                          { value: '', label: '—' },
                          { value: 'Spouse', label: t('Rel_spouse') },
                          { value: 'Partner', label: t('Rel_partner') },
                          { value: 'Relative', label: t('Rel_relative') },
                          { value: 'Parent', label: t('Rel_parent') },
                          { value: 'Child', label: t('Rel_child') },
                          { value: 'Sibling', label: t('Rel_sibling') },
                          { value: 'Friend', label: t('Rel_friend') },
                          { value: 'Other', label: t('Rel_other') },
                        ]} />
                    )}
                    {isFieldVisible('emergency_contact_phone') && <FREdit label="Phone" value={fv('emergency_contact_phone')} onChange={sf('emergency_contact_phone')} type="tel" placeholder="+44…" />}
                    <FREdit label={t('HrEcEmail') || 'Email'} value={fv('emergency_contact_email')} onChange={sf('emergency_contact_email')} type="email" />
                  </>
                ) : (
                  <>
                    {isFieldVisible('emergency_contact_name') && <FR label="Name" value={u.emergency_contact_name} />}
                    {isFieldVisible('emergency_contact_relationship') && <FR label="Relationship" value={u.emergency_contact_relationship} muted />}
                    {isFieldVisible('emergency_contact_phone') && <FR label="Phone" value={u.emergency_contact_phone} muted />}
                    <FR label={t('HrEcEmail') || 'Email'} value={u.emergency_contact_email} muted />
                  </>
                )}
              </Acc>
            )}

            <Acc icon="🪪" title={t('DriverLicense') || 'Driver License'} open={!!(license?.license_number || license?.vehicle_class)}>
              {canEdit ? (
                <>
                  <FREdit label={t('LicenseNumber') || 'License No.'} value={licenseForm.license_number || ''} onChange={v => setLicenseForm((p: any) => ({ ...p, license_number: v }))} />
                  <FREdit label={t('VehicleClass') || 'Category'} value={licenseForm.vehicle_class || ''}
                    onChange={v => setLicenseForm((p: any) => ({ ...p, vehicle_class: v }))}
                    options={[{ value: '', label: '—' }, ...['AM','A1','A2','A','B1','B','BE','C1','C1E','C','CE','D1','D1E','D','DE'].map(c => ({ value: c, label: c }))]} />
                  <FREdit label={t('IssueDate') || 'Issue date'} value={licenseForm.issue_date || ''} onChange={v => setLicenseForm((p: any) => ({ ...p, issue_date: v }))} type="date" />
                  <FREdit label={t('ExpiryDate') || 'Expiry date'} value={licenseForm.expiry_date || ''} onChange={v => setLicenseForm((p: any) => ({ ...p, expiry_date: v }))} type="date" />
                  {(['front', 'back'] as const).map(side => {
                    const url = license?.[`photo_${side}_url`];
                    const uploading = licenseUploading === side;
                    return (
                      <EF key={side} label={side === 'front' ? (t('PhotoFront') || 'Photo front') : (t('PhotoBack') || 'Photo back')}>
                        <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 6, minHeight: 70, borderRadius: 8, border: '1.5px dashed var(--border-input)', cursor: 'pointer', background: 'var(--btn-user-form)', position: 'relative', overflow: 'hidden' }}>
                          {url ? (
                            <>
                              <img src={`${baseUrl}${url}`} alt={side} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} />
                              <a href={`${baseUrl}${url}`} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,.5)', borderRadius: 4, padding: '2px 6px', color: '#fff', fontSize: 11, textDecoration: 'none', zIndex: 1 }}>{t('Open')}</a>
                            </>
                          ) : (
                            <span style={{ fontSize: 12, color: 'var(--text-color-calendar-week)', textAlign: 'center' }}>{uploading ? '…' : (t('ClickToUpload') || 'Click to upload')}</span>
                          )}
                          <input type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) uploadLicensePhoto(side, f); e.target.value = ''; }} />
                        </label>
                      </EF>
                    );
                  })}
                  <div style={{ padding: '10px 14px', display: 'flex', justifyContent: 'flex-end' }}>
                    <button type="button" onClick={saveLicense} style={{ padding: '5px 14px', borderRadius: 8, fontSize: 11, fontWeight: 700, background: '#2563eb', color: 'white', border: 'none', cursor: 'pointer' }}>{t('SaveLicense') || 'Save'}</button>
                  </div>
                </>
              ) : (
                <>
                  <FR label={t('LicenseNumber') || 'License No.'} value={license?.license_number} />
                  <FR label={t('VehicleClass') || 'Category'} value={license?.vehicle_class} muted />
                  <FR label={t('IssueDate') || 'Issue date'} value={fmtDate(license?.issue_date)} muted />
                  <FR label={t('ExpiryDate') || 'Expiry date'} value={fmtDate(license?.expiry_date)} muted />
                  {(['front', 'back'] as const).map(side => {
                    const url = license?.[`photo_${side}_url`];
                    return url ? (
                      <EF key={side} label={side === 'front' ? (t('PhotoFront') || 'Photo front') : (t('PhotoBack') || 'Photo back')}>
                        <a href={`${baseUrl}${url}`} target="_blank" rel="noreferrer">
                          <img src={`${baseUrl}${url}`} alt={side} style={{ maxWidth: '100%', maxHeight: 120, borderRadius: 6, objectFit: 'contain', display: 'block' }} />
                        </a>
                      </EF>
                    ) : null;
                  })}
                </>
              )}
            </Acc>

            {hasCustomFields && (
              <Acc icon="➕" title={t('AdditionalFields') || 'Additional Fields'} open={false}>
                <div style={{ padding: '6px 0' }}>
                  <CustomFieldValues tableName="hr_user" entityId={selectedUser.id} onFieldsLoaded={(count) => setHasCustomFields(count > 0)} />
                </div>
              </Acc>
            )}
          </>
        )}

        {/* ════ TAB: WORK ════ */}
        {tab === 'work' && (
          <>
            {isAdmin && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                {editing
                  ? <div style={{ display: 'flex', gap: 6 }}>
                      <button type="button" onClick={handleSaveProfile} style={{ padding: '5px 14px', borderRadius: 8, fontSize: 11, fontWeight: 700, background: '#2563eb', color: 'white', border: 'none', cursor: 'pointer' }}>Save</button>
                      <button type="button" onClick={() => { setEditing(false); fetchData(); }} style={{ padding: '5px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: 'var(--surface2)', color: 'var(--text-color-calendar)', border: '1px solid var(--border-new)', cursor: 'pointer' }}>Cancel</button>
                    </div>
                  : <button type="button" onClick={() => setEditing(true)} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 8, fontSize: 11, fontWeight: 700, color: 'var(--text-color-calendar)', background: 'var(--surface2)', border: '1px solid var(--border-new)', cursor: 'pointer' }}>✏️ {t('Edit') || 'Edit'}</button>
                }
              </div>
            )}

            {/* Недельный график из назначенного паттерна (read-only) — в самом верху, в обоих режимах */}
            <Acc icon="🗓️" title={t('HrSchedule') || 'Schedule'} open>
                {schedPattern && Array.isArray(schedPattern.days) && schedPattern.days.length === 7 ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr>
                      {[t('Day') || 'Day', t('Start') || 'Start', t('End') || 'End', t('HrBreak') || 'Break', t('Total') || 'Total'].map((h, i) => (
                        <th key={i} style={{ textAlign: i === 4 ? 'right' : 'left', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-color-calendar-week)', padding: '8px 14px', borderBottom: '1px solid var(--border2-new)' }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d, i) => {
                        const day = schedPattern.days[i] || {};
                        return (
                          <tr key={d}>
                            <td style={{ padding: '7px 14px', borderBottom: '1px solid var(--border2-new)', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-color-calendar)' }}>{t('WD_' + d) || d}</td>
                            {day.dayOff ? (
                              <td colSpan={3} style={{ padding: '7px 14px', borderBottom: '1px solid var(--border2-new)', fontSize: 12.5, fontStyle: 'italic', color: 'var(--text-color-calendar-week)' }}>{t('HrDayOff') || 'Day off'}</td>
                            ) : (
                              <>
                                <td style={{ padding: '7px 14px', borderBottom: '1px solid var(--border2-new)', fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}>{day.start || '—'}</td>
                                <td style={{ padding: '7px 14px', borderBottom: '1px solid var(--border2-new)', fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}>{day.end || '—'}</td>
                                <td style={{ padding: '7px 14px', borderBottom: '1px solid var(--border2-new)', fontSize: 12.5, color: 'var(--text-color-calendar)' }}>{day.breakMins || 0} {t('Min') || 'min'}</td>
                              </>
                            )}
                            <td style={{ padding: '7px 14px', borderBottom: '1px solid var(--border2-new)', fontSize: 12.5, fontWeight: 700, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{day.dayOff ? '—' : `${dayNet(day)}${t('HShort') || 'h'}`}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot><tr>
                      <td colSpan={4} style={{ padding: '8px 14px', textAlign: 'right', fontSize: 12, fontWeight: 700, borderTop: '1px solid var(--border-new)' }}>{t('HoursPerWeek') || 'Hours / week'}</td>
                      <td style={{ padding: '8px 14px', textAlign: 'right', fontSize: 13, fontWeight: 800, borderTop: '1px solid var(--border-new)', fontVariantNumeric: 'tabular-nums' }}>{schedPattern.hoursPerWeek ?? 0}{t('HShort') || 'h'}</td>
                    </tr></tfoot>
                  </table>
                ) : (
                  <div style={{ padding: '12px 14px', fontSize: 12.5, color: 'var(--text-color-calendar-week)' }}>
                    {schedPattern ? `${t('HoursPerWeek') || 'Hours / week'}: ${schedPattern.hoursPerWeek ?? 0}` : (t('Empty') || 'No pattern assigned')}
                  </div>
                )}
            </Acc>

            <Acc icon="🏢" title="Employment">
              {editing ? (
                <>
                  <EF label="Job title"><input title="Job title" style={inp} value={fv('job_title')} onChange={e => sf('job_title')(e.target.value)} /></EF>
                  <EF label="Department">
                    <select title="Department" style={sel} value={fv('departmentId') ?? ''} onChange={e => sf('departmentId')(e.target.value ? Number(e.target.value) : null)}>
                      <option value="">—</option>
                      {departments.map((d: any) => <option key={d.id} value={d.id}>{d.title}</option>)}
                    </select>
                  </EF>
                  {!hrMode && (
                    <EF label="Contract type">
                      <select title="Contract type" style={sel} value={fv('patternId') ?? ''} onChange={e => sf('patternId')(e.target.value ? Number(e.target.value) : null)}>
                        <option value="">—</option>
                        {patterns.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </EF>
                  )}
                  {!hrMode && <EF label="Start date"><CalendarPicker mode="single" variant="field" output="string" value={fv('contract_start_date')} onChange={(v) => sf('contract_start_date')(v)} /></EF>}
                  <EF label="Hours / week"><span style={{ fontSize: 13, color: 'var(--text-color-calendar-week)' }}>{form.patternHoursPerWeek != null ? `${form.patternHoursPerWeek} h` : '—'}</span></EF>
                  <EF label="Leave entitlement"><input title="Leave entitlement" type="number" style={inp} value={fv('min_leave_entitlement')} onChange={e => sf('min_leave_entitlement')(e.target.value)} /></EF>
                  <EF label="Vacation days"><input title="Vacation days" type="number" style={inp} value={fv('vacationDays')} onChange={e => sf('vacationDays')(e.target.value)} /></EF>
                  <EF label="License expiry"><CalendarPicker mode="single" variant="field" output="string" value={fv('license_expiry_date')} onChange={(v) => sf('license_expiry_date')(v)} /></EF>
                  <EF label="Licence number"><input title="Licence number" style={inp} value={fv('driving_licence_number')} onChange={e => sf('driving_licence_number')(e.target.value)} /></EF>
                  <EF label="Default car">
                    <select title="Default car" style={sel} value={fv('default_car_id') ?? ''} onChange={e => sf('default_car_id')(e.target.value ? Number(e.target.value) : null)}>
                      <option value="">—</option>
                      {carsList.map((c: any) => <option key={c.id} value={c.id}>{c.brand} {c.model} ({c.plate_number})</option>)}
                    </select>
                  </EF>
                  <EF label="Probation start"><CalendarPicker mode="single" variant="field" output="string" value={fv('probation_start_date')} onChange={(v) => sf('probation_start_date')(v)} /></EF>
                  <EF label="Probation end"><CalendarPicker mode="single" variant="field" output="string" value={fv('probation_end_date')} onChange={(v) => sf('probation_end_date')(v)} /></EF>
                  <EF label="Notice period"><input title="Notice period" style={inp} value={fv('notice_period')} onChange={e => sf('notice_period')(e.target.value)} placeholder="e.g. 1 month" /></EF>
                  <div style={{ padding: '10px 14px', display: 'flex', gap: 20 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}><input type="checkbox" title="Active" checked={!!form.isActive} onChange={e => sf('isActive')(e.target.checked)} />Active</label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}><input type="checkbox" title="Manager" checked={!!form.isManager} onChange={e => sf('isManager')(e.target.checked)} />Manager</label>
                  </div>
                </>
              ) : (
                <>
                  <FR label="Job title" value={u.job_title} />
                  <FR label="Department" value={deptName} muted />
                  <FR label="Working pattern" value={contractName} muted />
                  {!hrMode && <FR label="Start date" value={fmtDate(u.contract_start_date)} muted />}
                  <FR label="Hours / week" value={u.pattern?.hoursPerWeek ? `${u.pattern.hoursPerWeek}h` : null} muted />
                  <FR label="Leave entitlement" value={u.min_leave_entitlement ? `${u.min_leave_entitlement} days` : null} muted />
                  <FR label="Vacation days" value={u.vacationDays ? `${u.vacationDays} days` : null} muted />
                  <FR label="License expiry" value={fmtDate(u.license_expiry_date)} muted />
                  <FR label="Licence number" value={u.driving_licence_number} muted />
                  <FR label="Default car" value={(() => { const c = carsList.find((x: any) => x.id === u.default_car_id); return c ? `${c.brand} ${c.model}` : null; })()} muted />
                  <FR label="Probation start" value={fmtDate(u.probation_start_date)} muted />
                  <FR label="Probation end" value={fmtDate(u.probation_end_date)} muted />
                  <FR label="Notice period" value={u.notice_period} muted />
                </>
              )}
            </Acc>

            {/* Roles */}
            <Acc icon="🔑" title="Roles & Access">
              {editing ? (
                <div style={{ padding: '10px 14px' }}>
                  <Select isMulti menuPosition="fixed"
                    options={rolesList.map((r: any) => ({ value: r.id, label: r.title }))}
                    value={rolesList.filter((r: any) => (form.rolesId || []).includes(r.id)).map((r: any) => ({ value: r.id, label: r.title }))}
                    onChange={(s) => sf('rolesId')(s.map((x: any) => x.value))}
                    styles={slStyles} placeholder="Select roles…"
                  />
                </div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, padding: '10px 14px' }}>
                  {(u.roles || []).length > 0
                    ? (u.roles || []).map((r: any) => <span key={r.id} style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: 'var(--surface2)', color: 'var(--text-color)', border: '1px solid var(--border-new)' }}>{r.title}</span>)
                    : <span style={{ fontSize: 12, color: 'var(--text-color-calendar-week)', fontStyle: 'italic' }}>No roles</span>}
                </div>
              )}
            </Acc>

            {/* Supervisor */}
            <Acc icon="👤" title="Reports To" open={!!supervisor}>
              {editing ? (
                <div style={{ padding: '10px 14px' }}>
                  <Select isClearable menuPosition="fixed"
                    options={allUsersResolved.filter(us => us.id !== selectedUser.id).map(us => ({ value: us.id, label: `${us.first_name} ${us.last_name}` }))}
                    value={form.supervisorId ? { value: form.supervisorId, label: (() => { const sv = allUsersResolved.find(us => us.id === form.supervisorId); return sv ? `${sv.first_name} ${sv.last_name}` : ''; })() } : null}
                    onChange={(opt) => sf('supervisorId')(opt ? opt.value : null)}
                    styles={slStyles} placeholder="Select supervisor…"
                  />
                </div>
              ) : supervisor ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px' }}>
                  <UserAvatarUtil user={supervisor} baseUrl={baseUrl} size={34} />
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-color)' }}>{supervisor.first_name} {supervisor.last_name}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week)' }}>{supervisor.job_title}</div>
                  </div>
                </div>
              ) : (
                <div style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-color-calendar-week)', fontStyle: 'italic' }}>Not set</div>
              )}
            </Acc>

            {/* Team */}
            {team.length > 0 && (
              <Acc icon="👥" title={`Team (${team.length})`} open={false}>
                {team.slice(0, 8).map((m: any, i: number) => (
                  <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderBottom: i < team.length - 1 ? '1px solid var(--border2-new)' : 'none' }}>
                    <UserAvatarUtil user={m} baseUrl={baseUrl} size={30} />
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-color)' }}>{m.first_name} {m.last_name}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week)' }}>{m.job_title}</div>
                    </div>
                  </div>
                ))}
              </Acc>
            )}

            {/* Sensitive (заглушка) — в обоих режимах */}
            <Acc icon="🔒" title={t('HrTabSensitive') || 'Sensitive'} open={false}>
              <div style={{ padding: '10px 14px' }}>
                <div style={{ background: 'var(--surface2)', color: 'var(--text-color-calendar)', borderRadius: 10, padding: '10px 12px', fontSize: 12, fontWeight: 600, marginBottom: 10 }}>
                  🔒 {t('HrSensitiveStub') || 'Sensitive data is stored encrypted.'}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {['NINO', 'TaxCode', 'Passport', 'BankName', 'AccountHolder', 'AccountNumber', 'SortCode', 'IBAN'].map(f => (
                    <div key={f} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-color-calendar-week)' }}>{t('HrSens' + f) || f}</label>
                      <input title={f} style={inp} disabled value="" placeholder="••••••" />
                    </div>
                  ))}
                </div>
              </div>
            </Acc>
          </>
        )}

        {/* ════ TAB: SETTINGS ════ */}
        {tab === 'settings' && (
          <>
            {/* Working status — admin editing another user */}
            {!isSelf && isAdmin && (
              <Acc icon="🟢" title="Working Status">
                <div style={{ padding: '10px 14px' }}>
                  <select title="Working status" style={sel} value={form.workingStatusId ?? ''} onChange={e => sf('workingStatusId')(e.target.value ? Number(e.target.value) : null)}>
                    <option value="">—</option>
                    {workingStatuses.map((w: any) => <option key={w.id} value={w.id}>{w.emoji} {w.name}</option>)}
                  </select>
                </div>
              </Acc>
            )}

            <Acc icon="🔔" title="Notifications">
              {([
                ['mail_isPendingRep', 'Pending report submitted'],
                ['mail_isApprovedRep', 'Report approved'],
                ['mail_isDocument', 'Document uploaded'],
                ['mail_isWeekReminder', 'Weekly timesheet reminder'],
                ['mail_isToApproveReminder', 'Approve reminder (manager)'],
                ['mail_isExpensesPaid', 'Expense paid'],
                ['mail_isExpensesFulfilled', 'Expense fulfilled'],
                ['mail_isExpensesReject', 'Expense rejected'],
              ] as [keyof typeof notifs, string][]).map(([key, label]) => (
                <Tog key={key} label={label} checked={notifs[key]} onChange={v => setNotifs(p => ({ ...p, [key]: v }))} />
              ))}
            </Acc>

            {isSelf && (
              <>
                <Acc icon="🌍" title="Language & Region">
                  <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-color-calendar-week)', marginBottom: 6 }}>Interface Language</div>
                      <select style={sel} value={language} onChange={e => {
                        const lng = e.target.value;
                        setLanguage(lng);
                        i18n.changeLanguage(lng);
                        localStorage.setItem('language', lng);
                        localStorage.setItem('i18nextLng', lng);
                      }}>
                        <option value="en">🇬🇧 English</option>
                        <option value="ua">🇺🇦 Українська</option>
                        <option value="de">🇩🇪 Deutsch</option>
                      </select>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-color-calendar-week)', marginBottom: 6 }}>Timezone</div>
                      <select title="Timezone" style={sel} value={timezone} onChange={e => setTimezone(e.target.value)}>
                        <option value="">— Select timezone —</option>
                        {timezones.map(tz => <option key={tz.id} value={tz.name}>{tz.name} ({tz.utc_offset})</option>)}
                      </select>
                    </div>
                  </div>
                </Acc>

                <Acc icon="🔒" title="Security">
                  <div style={{ padding: '10px 14px' }}>
                    <div onClick={() => setShowPwd(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--surface2)', borderRadius: 10, cursor: 'pointer' }}>
                      <span style={{ fontSize: 16 }}>🔑</span>
                      <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: 'var(--text-color)' }}>Change password</span>
                      <span style={{ fontSize: 11, color: 'var(--text-color-calendar-week)' }}>{showPwd ? '▴' : '▾'}</span>
                    </div>
                    {showPwd && (
                      <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <input ref={pwdRef} type="password" placeholder="New password" style={inp} />
                        <input ref={pwdConfirmRef} type="password" placeholder="Confirm password" style={inp} />
                        <button type="button" onClick={handleChangePwd} style={{ padding: '9px', borderRadius: 9, background: '#2563eb', color: 'white', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>Update password</button>
                      </div>
                    )}
                  </div>
                </Acc>
              </>
            )}

            <button type="button" onClick={handleSaveSettings} disabled={savingSettings}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: 13, borderRadius: 12, background: 'linear-gradient(135deg,#059669,#10b981)', color: 'white', fontSize: 14, fontWeight: 800, border: 'none', cursor: 'pointer', boxShadow: '0 6px 20px rgba(16,185,129,.3)', marginTop: 6, opacity: savingSettings ? .7 : 1 }}>
              💾 Save settings
            </button>
          </>
        )}

        {/* ════ TAB: WORKING PATTERN (HR) — редактор таймлайна ════ */}
        {tab === 'pattern' && (
          <HrPatternTimeline
            userId={selectedUser.id}
            canEdit={isAdmin}
            onChanged={() => { fetchData(); refreshUsers?.(); }}
          />
        )}

        {/* ════ TAB: ABSENCES (HR) ════ */}
        {tab === 'absences' && (
          <Acc icon="🌴" title={t('HrAbsences') || 'Absences'}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                {[t('HrAbsenceType') || 'Type', t('Period') || 'Period', t('Hours') || 'Hours', t('Status') || 'Status'].map((h, i) => (
                  <th key={i} style={{ textAlign: 'left', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-color-calendar-week)', padding: '8px 14px', borderBottom: '1px solid var(--border2-new)' }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {absences.length === 0 ? (
                  <tr><td colSpan={4} style={{ padding: '14px', color: 'var(--text-color-calendar-week)', fontSize: 12.5 }}>{t('Empty') || 'No absences'}</td></tr>
                ) : absences.map((a: any) => (
                  <tr key={a.id}>
                    <td style={{ padding: '8px 14px', borderBottom: '1px solid var(--border2-new)' }}>
                      <span style={{ padding: '2px 9px', borderRadius: 20, fontSize: 10.5, fontWeight: 700, background: `${absColor(a.typeId)}22`, color: absColor(a.typeId) }}>{absTypeName(a.typeId)}</span>
                    </td>
                    <td style={{ padding: '8px 14px', borderBottom: '1px solid var(--border2-new)', fontSize: 12.5, color: 'var(--text-color)' }}>{a.startDate} → {a.endDate}</td>
                    <td style={{ padding: '8px 14px', borderBottom: '1px solid var(--border2-new)', fontSize: 12.5, color: 'var(--text-color)' }}>{a.hours}{t('HShort') || 'h'} / {a.days}{t('DShort') || 'd'}</td>
                    <td style={{ padding: '8px 14px', borderBottom: '1px solid var(--border2-new)' }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-color-calendar)' }}>{t('HrStatus_' + a.status) || a.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Acc>
        )}

        {/* ════ TAB: TOIL (HR) — тот же компонент истории, что в Timesheet → TOIL ════ */}
        {tab === 'toil' && (
          <UserToilHistory userId={selectedUser.id} />
        )}
      </div>

      {/* ── Плавающая Save (About): закреплена на месте, проявляется при скролле вниз ── */}
      {canEdit && tab === 'about' && (
        <button type="button" onClick={handleSaveProfile}
          style={{
            position: 'absolute', right: 16, bottom: 52, zIndex: 6,
            padding: '10px 24px', borderRadius: 12, background: 'linear-gradient(135deg,#2563eb,#4c1d95)',
            color: 'white', fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer',
            boxShadow: '0 6px 18px rgba(37,99,235,.4)',
            opacity: bodyScrolled ? 1 : 0,
            pointerEvents: bodyScrolled ? 'auto' : 'none',
            transition: 'opacity .2s ease',
          }}>
          Save
        </button>
      )}

      {/* ── FOOTER ── */}
      <div style={{ flexShrink: 0, padding: '8px 20px', borderTop: '1px solid var(--border2-new, rgba(0,10,60,.05))', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--surface2, rgba(255,255,255,.55))' }}>
        <span style={{ fontSize: 10, color: 'var(--text-color-calendar-week)' }}>
          {u.last_login ? `Last login: ${new Date(u.last_login).toLocaleString('en-GB')}` : ''}
        </span>
        {isSelf && (
          <button type="button" onClick={() => setShow(false)} style={{ fontSize: 11, fontWeight: 700, color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer' }}>
            My Profile →
          </button>
        )}
      </div>
    </div>

    {/* ── Reset password modal (HR) ── */}
    {showResetPwd && (
      <div onClick={() => setShowResetPwd(false)}
        style={{ position: 'fixed', inset: 0, zIndex: 'var(--z-modal)' as any, background: 'rgba(10,15,35,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <div onClick={e => e.stopPropagation()}
          style={{ width: 360, maxWidth: '100%', background: 'var(--bg-form)', borderRadius: 14, overflow: 'hidden', boxShadow: '0 24px 70px rgba(20,30,70,.35)' }}>
          <div style={{ padding: '14px 18px', background: 'linear-gradient(135deg,#4c1d95,#831843)', color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16 }}>🔑</span>
            <div style={{ flex: 1, fontSize: 14, fontWeight: 800 }}>{t('HrResetPassword') || 'Reset password'}</div>
            <button type="button" onClick={() => setShowResetPwd(false)} style={{ background: 'rgba(255,255,255,.15)', border: 'none', borderRadius: 7, color: '#fff', width: 26, height: 26, cursor: 'pointer' }}>✕</button>
          </div>
          <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 12, color: 'var(--text-color-calendar-week)' }}>
              {u.first_name} {u.last_name}
            </div>
            <input ref={pwdRef} type="password" placeholder={t('HrNewPassword') || 'New password'} style={inp} />
            <input ref={pwdConfirmRef} type="password" placeholder={t('ConfirmPassword') || 'Confirm password'} style={inp} />
            <button type="button" onClick={handleChangePwd}
              style={{ marginTop: 4, padding: '10px', borderRadius: 10, background: 'linear-gradient(135deg,#4c1d95,#831843)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
              {t('HrResetPassword') || 'Reset password'}
            </button>
          </div>
        </div>
      </div>
    )}

    </>
  );
};

export default ProfileCard;
